import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { getApiUrl, getDefaultHeaders } from '../../lib/api';

interface LoginFormData {
  username: string;
  password: string;
}

interface LoginFormErrors {
  username?: string;
  password?: string;
  general?: string;
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { login, setLoading } = useAuthStore();
  
  const [formData, setFormData] = useState<LoginFormData>({ 
    username: '', 
    password: '' 
  });
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setLoading(true);
    setErrors({});

    try {
      const url = getApiUrl('/api/auth/login');
      const headers = getDefaultHeaders();
      const payload = JSON.stringify(formData);
      
      console.log('🚀 Login attempt:', {
        url,
        headers,
        payload: { username: formData.username, password: '[REDACTED]' },
        environment: process.env.REACT_APP_ENV,
        apiUrl: process.env.REACT_APP_API_URL
      });

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: payload,
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get('content-type');
        
        console.log('❌ Response failed with status:', response.status);
        console.log('📄 Content-Type:', contentType);
        
        try {
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            console.log('❌ Error data:', errorData);
          } else {
            const textError = await response.text();
            console.log('❌ Error text:', textError);
            errorData = { detail: textError || `HTTP ${response.status}` };
          }
        } catch (parseError) {
          console.log('❌ Failed to parse error response:', parseError);
          errorData = { detail: `HTTP ${response.status} - ${response.statusText}` };
        }
        
        throw new Error(errorData.detail || errorData.message || 'Login failed');
      }

      const data = await response.json();
      console.log('✅ Login successful:', {
        hasToken: !!data.access_token,
        tokenType: data.token_type,
        user: data.user
      });
      
      // Store the token in the auth store
      login(data.access_token);
      
      // Redirect to dashboard on successful login
      navigate('/');
      
    } catch (error) {
      console.error('💥 Login error:', error);
      setErrors({ general: (error as Error).message });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof LoginFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: e.target.value 
    }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const testConnection = async () => {
    try {
      const url = getApiUrl('/health');
      console.log('🧪 Testing connection to:', url);
      const response = await fetch(url);
      console.log('🧪 Health check response:', response.status, await response.text());
    } catch (error) {
      console.error('🧪 Health check failed:', error);
    }
  };

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '15px', textAlign: 'center' }}>
        <a href="/debug" style={{ color: '#666', fontSize: '12px', textDecoration: 'none' }}>
          🔧 Debug Network Issues
        </a>
        <button 
          type="button"
          onClick={testConnection}
          style={{ 
            marginLeft: '10px',
            padding: '2px 8px', 
            fontSize: '10px', 
            backgroundColor: '#f0f0f0',
            border: '1px solid #ccc',
            borderRadius: '3px',
            cursor: 'pointer'
          }}
        >
          Test API
        </button>
      </div>
      <form onSubmit={handleSubmit} role="form">
        <div style={{ marginBottom: '20px' }}>
          <h2>Sign In</h2>
          
          {errors.general && (
            <div style={{ color: 'red', marginBottom: '10px' }}>
              {errors.general}
            </div>
          )}
          
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={formData.username}
              onChange={handleInputChange('username')}
              disabled={isLoading}
              required
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginTop: '5px',
                border: errors.username ? '1px solid red' : '1px solid #ccc'
              }}
            />
            {errors.username && (
              <div style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                {errors.username}
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              disabled={isLoading}
              required
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginTop: '5px',
                border: errors.password ? '1px solid red' : '1px solid #ccc'
              }}
            />
            {errors.password && (
              <div style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                {errors.password}
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      </form>
    </div>
  );
};